import getIntl from 'Helpers/intl'
import { logAppError } from 'Helpers/logger'

import {
  getWorkflowDetails,
  getRecipientByEmailOrSms,
  getRecipientDynamicFields,
  getConditionsDescription,
  getMessageDetails,
  getWaitingStepsDetails,
  getTotalCounters,
  getTasksStats,
  getExecutionDates,
  getMessagesStatsForSpecificRecipient
} from '../actions/actions'
import { pageState } from '../state/page'
import { resultsState, searchState } from '../state/search'
import {
  dynamicFieldsState,
  messageDetailsState,
  startConditionsDatesState,
  totalStatsState,
  workflowState
} from '../state/workflow'

export const initCallback =
  ({ set }) =>
  async (idWorkflow) => {
    set(pageState, (prev) => ({
      ...prev,
      loaded: false,
      loading: true
    }))

    try {
      const [{ name, steps }, recipientDynamicFields, conditionsDescription] =
        await Promise.all([
          getWorkflowDetails(idWorkflow),
          getRecipientDynamicFields(),
          getConditionsDescription(idWorkflow)
        ])

      const { stepsHaveTriggered } = await getTasksStats(steps)

      set(workflowState, (prev) => ({
        ...prev,
        name,
        steps,
        conditionsDescription,
        stepsHaveTriggered
      }))

      set(dynamicFieldsState, (prev) => [...prev, recipientDynamicFields])

      set(pageState, (prev) => ({ ...prev, title: name }))
    } catch (error) {
      logAppError({
        appError: error,
        Component: 'WorkflowStatistics',
        Method: 'initCallback',
        Data: JSON.stringify({ idWorkflow })
      })
      set(pageState, (prev) => ({
        ...prev,
        error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' })
      }))
    }
    set(pageState, (prev) => ({
      ...prev,
      loading: false,
      loaded: true
    }))
  }

export const submitSearchCallback =
  ({ set, snapshot, reset }) =>
  async () => {
    const { searchText } = await snapshot.getPromise(searchState)
    const { stepsHaveTriggered } = await snapshot.getPromise(workflowState)

    try {
      const recipient = await getRecipientByEmailOrSms(searchText)

      if (recipient.id > 0) {
        const stats = await getMessagesStatsForSpecificRecipient(
          recipient.id,
          stepsHaveTriggered
        )
        stats.map((stat) =>
          set(messageDetailsState(stat.idMessage), (prev) => ({
            ...prev,
            clicks: stat.result.totalClicks,
            viewOrDelivered: stat.result.totalOpenings,
            send: stat.result.totalSends,
            recipientExecutionDate: stat.result.lastExecutionDate
          }))
        )
      } else {
        stepsHaveTriggered.map(({ idMessage }) =>
          set(messageDetailsState(idMessage), (prev) => ({
            ...prev,
            clicks: 0,
            viewOrDelivered: 0,
            send: 0,
            recipientExecutionDate: undefined
          }))
        )
      }

      set(resultsState, (prev) => ({
        ...prev,
        id: recipient.id,
        recipientContact: recipient.recipientContact,
        subscribedOn: recipient.subscribedOn
      }))
      set(searchState, (prev) => ({
        ...prev,
        totalItems: recipient.totalItems
      }))
    } catch (error) {
      logAppError({
        appError: error,
        Component: 'WorkflowStatistics',
        Method: 'submitSearchCallback',
        Data: JSON.stringify({ searchText })
      })
      set(pageState, (prev) => ({
        ...prev,
        error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' })
      }))
    }
  }

export const initStepCallback =
  ({ set, snapshot }) =>
  async (idMessage, messageType, isFirst) => {
    set(messageDetailsState(idMessage), (prev) => ({
      ...prev,
      loading: true
    }))
    const {
      conditionsDescription: { WaitStep },
      steps,
      stepsHaveTriggered
    } = await snapshot.getPromise(workflowState)
    const { ids } = await snapshot.getPromise(totalStatsState(messageType))

    try {
      const [{ subject }, { cu, aui, ei, rec }] = await Promise.all([
        getMessageDetails(idMessage, messageType),
        getTotalCounters(idMessage, messageType)
      ])

      const { hasTriggered, idTask } = stepsHaveTriggered.find(
        (step) => step.idMessage === idMessage
      ) || { hasTriggered: false, idTask: 0 }

      set(messageDetailsState(idMessage), (prev) => ({
        ...prev,
        subject,
        messageType,
        clicks: cu,
        viewOrDelivered: aui,
        send: ei,
        rec,
        hasTriggered,
        idTask,
        isFirst
      }))

      if (isFirst && idTask) {
        const { firstExecutionDate, lastExecutionDate } =
          await getExecutionDates(idTask)
        set(startConditionsDatesState, (prev) => ({
          ...prev,
          firstExecutionDate,
          lastExecutionDate
        }))
      }

      const isAlreadyCounted = ids.find((id) => id === idMessage)
      if (!isAlreadyCounted) {
        set(totalStatsState(messageType), (prev) => {
          return {
            ...prev,
            ids: [...prev.ids, idMessage],
            totalClicks: prev.totalClicks + cu,
            totalViewOrDelivered: prev.totalViewOrDelivered + aui,
            totalSend: prev.totalSend + ei,
            totalRec: prev.totalRec + rec
          }
        })
      }

      const timeConditions = steps.filter(
        (step) =>
          step.timeCondition &&
          step.message &&
          step.message.IdMessage === idMessage
      )
      if (timeConditions.length) {
        const {
          IdStep: timeConditionId,
          Description: timeConditionDescription
        } = await getWaitingStepsDetails(
          timeConditions[0].timeCondition,
          WaitStep
        )
        set(messageDetailsState(idMessage), (prev) => ({
          ...prev,
          timeConditionId,
          timeConditionDescription
        }))
      }
    } catch (error) {
      logAppError({
        appError: error,
        Component: 'WorkflowStatistics',
        Method: 'initStepCallback',
        Data: JSON.stringify({ idMessage, messageType })
      })
      set(pageState, (prev) => ({
        ...prev,
        error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' })
      }))
    }
    set(messageDetailsState(idMessage), (prev) => ({
      ...prev,
      loading: false
    }))
  }

export const resetSearchCallback =
  ({ reset, set, snapshot }) =>
  async () => {
    const { stepsHaveTriggered } = await snapshot.getPromise(workflowState)

    stepsHaveTriggered.map(async (step) => {
      const { messageType } = await snapshot.getPromise(
        messageDetailsState(step.idMessage)
      )
      const { cu, aui, ei } = await getTotalCounters(
        step.idMessage,
        messageType
      )
      set(messageDetailsState(step.idMessage), (prev) => ({
        ...prev,
        clicks: cu,
        viewOrDelivered: aui,
        send: ei,
        recipientExecutionDate: undefined
      }))
    })

    reset(searchState)
    reset(resultsState)
  }

export const cleanupCallback =
  ({ reset }) =>
  async () => {
    reset(pageState)
    reset(workflowState)
    reset(searchState)
    reset(resultsState)
  }
